import {
  ApiError,
  ApiErrorInitialState,
  NotificationRequest,
  NotificationSubscription,
  NotificationSubscriptionWithSubjectInfo,
} from '@hellodarwin/core/lib/features/entities';
import {
  EntityState,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import showErrorNotification from '../../utils/show-error-notifications';
import ClientApiV2 from '../client-api-v2';

const notificationSubscriptionAdapter = createEntityAdapter({
  selectId: (model: NotificationSubscriptionWithSubjectInfo) =>
    model.notification_subscription_id,
});

type StatusType = 'generic' | 'fetchSubscribedNotifications';

type Status = {
  [key in StatusType]: 'idle' | 'pending';
};

export interface GrantNotificationsState {
  status: 'idle' | 'pending';
  mappedStatus: Status;
  error: ApiError;
  subscribedNotification: EntityState<
    NotificationSubscriptionWithSubjectInfo,
    string
  >;
}

const initialState: GrantNotificationsState = {
  status: 'idle',
  mappedStatus: {
    generic: 'idle',
    fetchSubscribedNotifications: 'idle',
  },
  error: ApiErrorInitialState,
  subscribedNotification: notificationSubscriptionAdapter.getInitialState(),
};

export const subscribeNotifications = createAsyncThunk<
  NotificationSubscription[],
  { api: ClientApiV2; request: NotificationRequest },
  { rejectValue: ApiError }
>(
  'admin/subscribeNotifications',
  async (
    {
      api,
      request,
    }: {
      api: ClientApiV2;
      request: NotificationRequest;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<NotificationSubscription[]>(
        '/notifications/subscribe',
        request,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const unsubscribeNotifications = createAsyncThunk<
  string[],
  { api: ClientApiV2; request: NotificationRequest },
  { rejectValue: ApiError }
>(
  'admin/unsubscribeNotifications',
  async (
    { api, request }: { api: ClientApiV2; request: NotificationRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.delete<string[]>('/notifications/subscribe', {
        data: request,
      });
      return response?.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchSubscribedNotifications = createAsyncThunk<
  NotificationSubscriptionWithSubjectInfo[],
  { api: ClientApiV2; locale: string },
  { rejectValue: ApiError }
>(
  'admin/fetchSubscribedNotifications',
  async (
    { api, locale }: { api: ClientApiV2; locale: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<NotificationSubscriptionWithSubjectInfo[]>(
        `/notifications?locale=${locale}`,
      );
      return response.data;
    } catch (err: any) {
      console.error(err.response.data);
      showErrorNotification(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

const grantNotificationsSlice = createSlice({
  name: 'grantNotifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(subscribeNotifications.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(subscribeNotifications.fulfilled, (state, { payload }) => {
      if (payload && payload.length > 0) {
        for (const subscription of payload) {
          if (subscription.paused_until === undefined) {
            subscription.paused_until = undefined;
          }
          state.subscribedNotification =
            notificationSubscriptionAdapter.upsertOne(
              state.subscribedNotification,
              subscription,
            );
        }
      }
      state.status = 'idle';
    });
    builder.addCase(subscribeNotifications.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });
    builder.addCase(unsubscribeNotifications.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(
      unsubscribeNotifications.fulfilled,
      (state, { payload }) => {
        if (payload && payload.length > 0) {
          state.subscribedNotification =
            notificationSubscriptionAdapter.removeMany(
              state.subscribedNotification,
              payload,
            );
        }
        state.status = 'idle';
      },
    );
    builder.addCase(unsubscribeNotifications.rejected, (state, { payload }) => {
      state.error = payload ?? ApiErrorInitialState;
      state.status = 'idle';
    });
    builder.addCase(fetchSubscribedNotifications.pending, (state) => {
      state.mappedStatus.fetchSubscribedNotifications = 'pending';
    });
    builder.addCase(
      fetchSubscribedNotifications.fulfilled,
      (state, { payload }) => {
        if (payload && payload.length > 0) {
          state.subscribedNotification =
            notificationSubscriptionAdapter.upsertMany(
              state.subscribedNotification,
              payload,
            );
        }
        state.mappedStatus.fetchSubscribedNotifications = 'idle';
      },
    );
    builder.addCase(
      fetchSubscribedNotifications.rejected,
      (state, { payload }) => {
        state.error = payload ?? ApiErrorInitialState;
        state.mappedStatus.fetchSubscribedNotifications = 'idle';
      },
    );
  },
});

export const selectGrantNotificationLoading = (state: RootState) =>
  state.grantNotifications.status === 'pending';

export const selectSubscribedGrantNotifications = (state: RootState) =>
  state.grantNotifications.subscribedNotification.ids
    .map((id) => state.grantNotifications.subscribedNotification.entities[id])
    .filter((n) => n && n.object_type === 'grant');

export const selectSubscribedNotificationByGrantId =
  (grantId: string) => (state: RootState) =>
    state.grantNotifications.subscribedNotification.ids
      .map((id) => state.grantNotifications.subscribedNotification.entities[id])
      .filter((n) => n && n.grant_id === grantId);

export const selectSubscribedNotificationLoading = (state: RootState) =>
  state.grantNotifications.mappedStatus.fetchSubscribedNotifications ===
  'pending';

export const {
  selectAll: selectAllSubscribedNotifications,
  selectById: selectSubscribedNotificationById,
} = notificationSubscriptionAdapter.getSelectors(
  (state: RootState) => state.grantNotifications.subscribedNotification,
);

export const grantNotificationsReducer = grantNotificationsSlice.reducer;
